import type { ItemProps, Variable, HomeyAPI } from 'homey-api';

import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';
import { KeyedAsyncQueue } from '../KeyedAsyncQueue.ts';
import {
  OptimisticEntityUpdates,
  type OptimisticEntityMutationResult,
} from '../OptimisticEntityUpdates.ts';

export type VariableProps = ItemProps<Variable>;

export type VariablesData = { [key: string]: Variable };
export type VariablesById = { [key: string]: VariableProps };

export type VariableUpdate = HomeyAPI.ManagerLogic.UpdateVariableOptions['variable'];

type VariableOptimisticOperation = {
  type: 'update';
  variable: VariableUpdate;
};

interface State {
  data: VariablesData | null;
  byId: VariablesById | null;
  isLoading: boolean;
  error: unknown;
}

export type VariablesStoreState = State;

export class VariablesStore extends BaseApiStore<State, Variable> {
  #optimisticUpdates = new OptimisticEntityUpdates<VariableProps, VariableOptimisticOperation>({
    applyOperation: VariablesStore.#applyOptimisticOperation,
    reconcileMutationResult: VariablesStore.#reconcileOptimisticMutationResult,
  });
  #persistenceQueue = new KeyedAsyncQueue<string>();

  constructor() {
    super('variables');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ manager: api.logic });

      if (abortController.signal.aborted) return;

      const baseRevision = this.#optimisticUpdates.captureBaseRevision();
      const variablesPromise = api.logic.getVariables({
        $cache: cache,
      });
      const [variablesData] = await Promise.all([variablesPromise]);

      if (abortController.signal.aborted) return;

      const variables = Object.values(variablesData);

      const data: VariablesData = {};
      const byId: VariablesById = {};

      for (const variableInstance of variables) {
        const variableReference = variableInstance.getProps();

        data[variableReference.id] = variableInstance;
        byId[variableReference.id] = variableReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: this.#optimisticUpdates.reconcileBaseSnapshot(baseRevision, byId),
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy(reason: string) {
    this.disconnectManager().catch(console.error);

    this.#optimisticUpdates.clear();
    this.#persistenceQueue.clear();
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.logic.on('variable.create', this.handleCreate);
    api.logic.on('variable.update', this.handleUpdate);
    api.logic.on('variable.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.logic.off('variable.create', this.handleCreate);
    api.logic.off('variable.update', this.handleUpdate);
    api.logic.off('variable.delete', this.handleDelete);
  }

  getInstanceFromCache(variable: { id: string }) {
    return this.getCache()[variable.id];
  }

  getCache(): Readonly<VariablesData> {
    const api = this.getApi();

    return api.logic.getCachedItems('variable');
  }

  handleCreate = (createdVariable: Variable) => {
    this.DEBUG && console.info(`create:variable:${createdVariable.id}`);
    const state = this.get();
    const variableInstance = this.getInstanceFromCache(createdVariable);

    if (variableInstance == null) return;

    const variableReference = this.#optimisticUpdates.reconcileBaseEntity(
      createdVariable.id,
      variableInstance.getProps(),
    );
    const nextById = { ...state.byId };

    if (variableReference == null) {
      delete nextById[createdVariable.id];
    } else {
      nextById[createdVariable.id] = variableReference;
    }

    this.set({
      data: {
        ...state.data,
        [createdVariable.id]: variableInstance,
      },
      byId: nextById,
    });
  };

  handleUpdate = (updatedVariable: Variable) => {
    this.DEBUG && console.info(`update:variable:${updatedVariable.id}`);

    this.addToQueue(updatedVariable.id, updatedVariable);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedVariable] of Object.entries(updateQueue)) {
        const variableInstance = this.getInstanceFromCache(queuedVariable);

        if (state.data == null) {
          this.DEBUG && console.info(`update:variable:missing-data`);
          continue;
        }

        if (variableInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:variable:unknown:${id}`);
          continue;
        }

        const variableReference = this.#optimisticUpdates.reconcileBaseEntity(
          id,
          variableInstance.getProps(),
        );

        if (variableReference == null) {
          delete nextById[id];
        } else {
          nextById[id] = variableReference;
        }

        updateCount++;
      }

      this.DEBUG && console.info(`update:variable:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedVariable: Pick<Variable, 'id'>) => {
    this.DEBUG && console.info(`delete:variable:${deletedVariable.id}`);
    const state = this.get();

    if (state.data == null) {
      this.DEBUG && console.log(`delete:variable:missing-data`);
      return;
    }

    const variableInstance = state.data[deletedVariable.id];

    if (variableInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[variableInstance.id];
    const optimisticVariable = this.#optimisticUpdates.reconcileBaseEntity(
      variableInstance.id,
      undefined,
    );

    if (optimisticVariable == null) {
      delete nextById[variableInstance.id];
    } else {
      nextById[variableInstance.id] = optimisticVariable;
    }

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const baseRevision = this.#optimisticUpdates.captureBaseRevision();
    const cache = this.getCache();
    const variables = Object.values(cache ?? {});
    const data: VariablesData = {};
    const byId: VariablesById = {};

    for (const variableInstance of variables) {
      const variableReference = variableInstance.getProps();
      data[variableReference.id] = variableInstance;
      byId[variableReference.id] = variableReference;
    }

    this.set({
      data: data,
      byId: this.#optimisticUpdates.reconcileBaseSnapshot(baseRevision, byId),
    });
  }

  async updateVariableOptimistically({
    id,
    variable,
  }: {
    id: string;
    variable: VariableUpdate;
  }): Promise<void> {
    const currentVariable = this.get().byId?.[id];

    if (currentVariable == null) {
      throw new Error(`Unknown variable: ${id}`);
    }

    const api = this.getApi();
    const operation: VariableOptimisticOperation = { type: 'update', variable };
    const { mutationId, entity: optimisticVariable } = this.#optimisticUpdates.beginMutation(
      id,
      currentVariable,
      operation,
    );

    this.#publishVariable(id, optimisticVariable);

    await this.#persistenceQueue.enqueue(id, async () => {
      this.#optimisticUpdates.startPersistence(id, mutationId);

      try {
        const updatedVariable = await api.logic.updateVariable({ id, variable });

        this.#finishOptimisticMutation(id, mutationId, {
          status: 'success',
          baseEntity: updatedVariable.getProps(),
        });
      } catch (error) {
        this.#finishOptimisticMutation(id, mutationId, { status: 'failure' });
        throw error;
      }
    });
  }

  getVariables() {
    return this.get().byId;
  }

  #finishOptimisticMutation(
    variableId: string,
    mutationId: number,
    result: OptimisticEntityMutationResult<VariableProps>,
  ): void {
    const finishResult = this.#optimisticUpdates.finishMutation(variableId, mutationId, result);

    if (finishResult.status === 'ignored') {
      return;
    }

    this.#publishVariable(variableId, finishResult.entity);
  }

  #publishVariable(variableId: string, variable: VariableProps | undefined): void {
    const state = this.get();

    if (state.byId == null) {
      return;
    }

    const nextById = { ...state.byId };

    if (variable == null) {
      delete nextById[variableId];
    } else {
      nextById[variableId] = variable;
    }

    this.set({ byId: nextById });
  }

  static #applyOptimisticOperation(
    variable: VariableProps | undefined,
    operation: VariableOptimisticOperation,
  ): VariableProps | undefined {
    if (variable == null) {
      return undefined;
    }

    return Object.assign({}, variable, operation.variable);
  }

  static #reconcileOptimisticMutationResult(
    baseVariable: VariableProps | undefined,
    mutationVariable: VariableProps | undefined,
    operation: VariableOptimisticOperation,
  ): VariableProps | undefined {
    if (baseVariable == null || mutationVariable == null) {
      return undefined;
    }

    return Object.assign(
      {},
      baseVariable,
      'name' in operation.variable ? { name: mutationVariable.name } : {},
      'value' in operation.variable ? { value: mutationVariable.value } : {},
    );
  }
}

export const variablesStore = new VariablesStore();
